import AvailableDomainBody from "components/availableDomainBody/AvailableDomainBody";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import PurchaseDropDown from "components/purchaseDropDown/PurchaseDropDown";
import TakenDomainBody from "components/takenDomainBody/TakenDomainBody";
import WhoisButton from "components/whoisButton/WhoisButton";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import Helmet from "components/headHelmet/HeadHelmet";
import { useParams } from "react-router-dom";
import domainSlice from "reducers/domainSlice";
import {
  domainDetailsSelector,
  getDomainDetailsErrorSelector,
  gettingDomainDetailsSelector,
  resellersSelector,
} from "selectors/domainSelectors";
import styled from "styled-components";
import { getSiteUrl } from "utility/urlUtility";

const StyledPageTitle = styled(PageTitle)<{
  $registered: boolean;
  $loaded: boolean;
}>`
  flex: 1;
  margin-bottom: 0;
  color: ${(props: any) =>
    props.$loaded ? (props.$registered ? "#c60404" : "#21b400") : "#222222"};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 48px;
  margin-bottom: 10px;

  @media only screen and (max-width: 992px) {
    padding-top: 10px;
  }

  @media only screen and (max-width: 500px) {
    flex-direction: column;
    > div {
      margin-top: 20px;
      margin-bottom: 10px;
    }
  }
`;

const Status = styled.div``;

const SpinnerWrapper = styled.div`
  color: #28c604;
  font-size: initial;

  > div {
    font-size: 1.5rem;
    width: 30px;
    height: 30px;
  }
`;

const DomainPage = () => {
  const { domain } = useParams();
  const dispatch = useAppDispatch();

  const [records, setRecords] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    if (domain) dispatch(domainSlice.actions.getDomainDetails(domain));
  }, [dispatch, domain]);

  const gettingDomainDetails = useAppSelector(gettingDomainDetailsSelector);
  const domainDetails = useAppSelector(domainDetailsSelector);
  const getDomainDetailsError = useAppSelector(getDomainDetailsErrorSelector);
  const resellers = useAppSelector(resellersSelector);

  const handleOnRecodsLoaded = useCallback((r: string[]) => setRecords(r), []);

  const titleLoaded = useMemo(
    () =>
      gettingDomainDetails === false && domainDetails?.registered !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const HeaderActions = useMemo(() => {
    return (
      <Status>
        {gettingDomainDetails === true && (
          <SpinnerWrapper>
            <Spinner size="sm" />
          </SpinnerWrapper>
        )}

        {domain && !gettingDomainDetails && domainDetails?.registered && (
          <WhoisButton
            records={records}
            name={domain}
            onRecordsLoaded={handleOnRecodsLoaded}
          />
        )}

        {domain &&
          !gettingDomainDetails &&
          !domainDetails?.registered &&
          resellers && (
            <PurchaseDropDown resellers={resellers} domain={domain} />
          )}
      </Status>
    );
    // domain, gettingDomainDetails, domainDetails, resellers, records may change frequently
    // but this is a small memo to keep JSX tidy; React will still re-evaluate when deps change.
  }, [
    domain,
    gettingDomainDetails,
    domainDetails,
    resellers,
    records,
    handleOnRecodsLoaded,
  ]);

  const DomainBody = useMemo(() => {
    if (!domain || gettingDomainDetails || !domainDetails) return null;
    return domainDetails?.registered ? (
      <TakenDomainBody domainDetails={domainDetails} />
    ) : (
      <AvailableDomainBody domainDetails={domainDetails} />
    );
  }, [domain, gettingDomainDetails, domainDetails]);

  const isThinPage = useMemo(() => {
    if (!domainDetails) return true;

    // Thin page = domain is available (not registered) and there are no business ideas
    return (
      !!domain &&
      domainDetails.registered === false &&
      (!domainDetails.businessIdeas || domainDetails.businessIdeas.length === 0)
    );
  }, [domain, domainDetails]);

  const detailsLoaded = useMemo(
    () => gettingDomainDetails === false && domainDetails !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const prerenderedRobots = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document
      .querySelector('meta[name="robots"]')
      ?.getAttribute("content");
  }, []);

  const shouldIndex = useMemo(() => {
    if (!detailsLoaded || !domainDetails) return null;

    if (typeof domainDetails.isIndexable === "boolean") {
      return domainDetails.isIndexable;
    }

    // Backward-compatible fallback while API rolls out isIndexable.
    return !isThinPage;
  }, [detailsLoaded, domainDetails, isThinPage]);

  const robotsContent = useMemo(() => {
    if (shouldIndex === null) {
      return prerenderedRobots ?? "noindex,follow";
    }

    return shouldIndex ? "index,follow" : "noindex,follow";
  }, [shouldIndex, prerenderedRobots]);

  const metaTitle = useMemo(() => {
    if (!domainDetails) return `${domain} - Domain Availability | Domain Findr`;
    return domainDetails.registered
      ? `${domain} - Registered Domain | Domain Findr`
      : `${domain} - Available Domain for Sale | Domain Findr`;
  }, [domain, domainDetails]);

  const metaDescription = useMemo(() => {
    if (!domainDetails)
      return `Check availability and details for ${domain}. Find domain registration info, business ideas, and purchase options.`;

    if (domainDetails.registered) {
      return `${domain} is currently registered. View WHOIS information, registration details, and ownership data.`;
    }

    const ideasCount = domainDetails.businessIdeas?.length || 0;
    if (ideasCount > 0) {
      return `${domain} is available for purchase! Explore ${ideasCount} business idea${
        ideasCount > 1 ? "s" : ""
      } and find the best registrar to buy this domain.`;
    }

    return `${domain} is available for registration. Compare prices from top domain registrars and secure this domain today.`;
  }, [domain, domainDetails]);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`${getSiteUrl()}/domain/${domain}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${getSiteUrl()}/domain/${domain}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta
          property="twitter:url"
          content={`${getSiteUrl()}/domain/${domain}`}
        />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />

        {/* Robots is API-driven (with thin-page fallback) and independent of render mode. */}
        {robotsContent && <meta name="robots" content={robotsContent} />}
      </Helmet>
      <Page collapsedView={true} maxWidth={800}>
        <PageWrapper>
          <Header>
            <StyledPageTitle
              $registered={domainDetails?.registered ?? false}
              $loaded={titleLoaded}
              align="left"
            >
              {domain}
            </StyledPageTitle>
            {HeaderActions}
          </Header>
          {getDomainDetailsError && (
            <Alert className="mt-3" variant="danger">
              {getDomainDetailsError}
            </Alert>
          )}
          {DomainBody}
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(DomainPage);
